import React, { useState, useEffect, useCallback } from 'react';
import { Pencil, Copy, Share2, Trash2, Search, PackageOpen, Send } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import { listMaterials, getMaterial, mirrorMaterial, deleteMaterial } from './materialApi';
import TransferDialog from './TransferDialog';

/**
 * MaterialList — Uebersicht angelegter Materialien.
 * Aktionen: Bearbeiten, „Als Vorlage nutzen" (Felder vorbefuellen), in Katalog
 * spiegeln, loeschen.
 *
 * Props: user, onEdit(material), onUseAsTemplate(material), showSuccess, showError, refreshKey
 */
const MaterialList = ({ user, onEdit, onUseAsTemplate, showSuccess, showError, refreshKey }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [transferTarget, setTransferTarget] = useState(null); // Material für SAP-Transfer-Dialog

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listMaterials(user, { status: statusFilter, search });
      setRows(res.materials || []);
    } catch (e) {
      showError(t('material.listError'));
    } finally {
      setLoading(false);
    }
  }, [user, statusFilter, search, showError, t]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const handleTemplate = async (id) => {
    try {
      const { material } = await getMaterial(user, id);
      const { id: _omit, status: _s, mirrored_product_id: _m, ...rest } = material;
      onUseAsTemplate?.({ ...rest, id: null, status: 'draft', reference_matnr: material.matnr || `#${id}` });
    } catch { showError(t('material.loadError')); }
  };

  const handleEdit = async (id) => {
    try {
      const { material } = await getMaterial(user, id);
      onEdit?.(material);
    } catch { showError(t('material.loadError')); }
  };

  const handleMirror = async (id) => {
    try {
      await mirrorMaterial(user, id);
      showSuccess(t('material.mirrorSuccess'));
      load();
    } catch (e) { showError(t('material.mirrorError')); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('material.confirmDelete'))) return;
    try {
      await deleteMaterial(user, id);
      showSuccess(t('material.deleteSuccess'));
      load();
    } catch { showError(t('material.deleteError')); }
  };

  const cardCls = isDarkMode
    ? 'bg-gray-900/30 border border-white/10 backdrop-blur-xl'
    : 'bg-white border border-slate-200';
  const inputBase = isDarkMode
    ? 'bg-white/5 border-white/15 text-white placeholder-white/30'
    : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400';

  const statusBadge = (s) => {
    const map = {
      draft:       isDarkMode ? 'bg-amber-500/15 text-amber-300 border-amber-400/20'   : 'bg-amber-50 text-amber-700 border-amber-200',
      ready:       isDarkMode ? 'bg-emerald-500/15 text-emerald-300 border-emerald-400/20' : 'bg-emerald-50 text-emerald-700 border-emerald-200',
      transferred: isDarkMode ? 'bg-blue-500/15 text-blue-300 border-blue-400/20'      : 'bg-blue-50 text-blue-700 border-blue-200',
    };
    return (
      <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium border ${map[s] || map.draft}`}>
        {t(`material.status_${s}`)}
      </span>
    );
  };

  const iconBtn = (onClick, Icon, title, danger) => (
    <button type="button" title={title} onClick={onClick}
      className={`p-1.5 rounded-lg transition-colors ${
        danger
          ? (isDarkMode ? 'text-red-300/70 hover:text-red-300 hover:bg-red-500/10' : 'text-red-500 hover:bg-red-50')
          : (isDarkMode ? 'text-white/60 hover:text-white hover:bg-white/10' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100')
      }`}>
      <Icon className="w-4 h-4" />
    </button>
  );

  return (
    <div className="space-y-4">
      {/* Filterleiste */}
      <div className={`rounded-2xl p-3 flex flex-wrap items-center gap-3 ${cardCls}`}>
        <div className="relative flex-1 min-w-[200px]">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-white/30' : 'text-slate-400'}`} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('material.searchPlaceholder')}
            className={`w-full rounded-lg border pl-9 pr-3 py-2 text-sm outline-none ${inputBase}`} />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className={`rounded-lg border px-3 py-2 text-sm outline-none ${inputBase}`}>
          <option value="">{t('material.allStatus')}</option>
          <option value="draft">{t('material.status_draft')}</option>
          <option value="ready">{t('material.status_ready')}</option>
          <option value="transferred">{t('material.status_transferred')}</option>
        </select>
      </div>

      {/* Tabelle / Leerstaaten */}
      <div className={`rounded-2xl overflow-hidden ${cardCls}`}>
        {loading ? (
          <div className={`p-10 text-center text-sm ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`}>{t('common.loading')}</div>
        ) : rows.length === 0 ? (
          <div className={`p-12 text-center ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
            <PackageOpen className="w-10 h-10 mx-auto mb-3 opacity-60" />
            <div className="text-sm">{t('material.emptyList')}</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={isDarkMode ? 'text-white/50 border-b border-white/10' : 'text-slate-500 border-b border-slate-200'}>
                  <th className="text-left font-medium px-4 py-2.5">{t('material.matnr')}</th>
                  <th className="text-left font-medium px-4 py-2.5">{t('material.description')}</th>
                  <th className="text-left font-medium px-4 py-2.5">{t('material.mtart')}</th>
                  <th className="text-left font-medium px-4 py-2.5">{t('material.matkl')}</th>
                  <th className="text-left font-medium px-4 py-2.5">{t('material.fillCol')}</th>
                  <th className="text-left font-medium px-4 py-2.5">{t('material.statusCol')}</th>
                  <th className="text-right font-medium px-4 py-2.5">{t('material.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((m) => (
                  <tr key={m.id} className={isDarkMode ? 'border-b border-white/5 hover:bg-white/5' : 'border-b border-slate-100 hover:bg-slate-50'}>
                    <td className={`px-4 py-2.5 font-mono text-xs ${isDarkMode ? 'text-white/80' : 'text-slate-700'}`}>{m.matnr || `#${m.id}`}</td>
                    <td className={`px-4 py-2.5 ${isDarkMode ? 'text-white/90' : 'text-slate-800'}`}>{m.maktx || '—'}</td>
                    <td className={`px-4 py-2.5 ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>{m.mtart || '—'}</td>
                    <td className={`px-4 py-2.5 ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>{m.matkl || '—'}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className={`w-16 h-1.5 rounded-full overflow-hidden ${isDarkMode ? 'bg-white/10' : 'bg-slate-200'}`}>
                          <div className="h-full bg-emerald-500" style={{ width: `${m.total ? Math.round((m.filled / m.total) * 100) : 0}%` }} />
                        </div>
                        <span className={`text-[11px] tabular-nums ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`}>{m.filled ?? 0}/{m.total ?? 0}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5">{statusBadge(m.status)}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center justify-end gap-1">
                        {iconBtn(() => handleEdit(m.id), Pencil, t('material.edit'))}
                        {iconBtn(() => handleTemplate(m.id), Copy, t('material.useAsTemplate'))}
                        {m.status === 'ready' && iconBtn(() => setTransferTarget(m), Send, t('material.transfer'))}
                        {!m.mirrored_product_id && m.status !== 'draft' && iconBtn(() => handleMirror(m.id), Share2, t('material.mirror'))}
                        {iconBtn(() => handleDelete(m.id), Trash2, t('material.delete'), true)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {transferTarget && (
        <TransferDialog
          user={user}
          materialId={transferTarget.id}
          materialLabel={transferTarget.maktx || transferTarget.matnr || `#${transferTarget.id}`}
          onClose={() => setTransferTarget(null)}
          onDone={() => { setTransferTarget(null); load(); }}
          showSuccess={showSuccess}
          showError={showError}
        />
      )}
    </div>
  );
};

export default MaterialList;
